import React from 'react';
import { Button, Card, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom';

import { tInsuranceTypeEnum } from '@models/InsuranceType';
import { InsurancePolicyList } from '@models/InsurancePolicy';
import { InsurancePolicyItemList } from '@models/InsurancePolicyItem';
import { getOrderStatusVariant } from '@models/Order';
import { InsurerLogo } from '@components/Feature/Insurer';
import { getInsurerHotlines } from '@models/Insurer';
import { InsuranceTypesEnum } from '@models/InsuranceType';
import { formatDate } from '@utils/datetime';
import { formatCurrency } from '@utils/currency';

import { RowItem } from './row-item';
import { routeUserDashboardInsurancePolicyClaim, routeUserDashboardInsurancePolicyUserDetailsPage } from '../route';

type Props = {
  policyItem: InsurancePolicyItemList;
};

const PolicyItemItem = ({ policyItem }: Props) => {
  const { t } = useTranslation();
  const {
    id,
    insurancePolicyId,
    insuranceType,
    name,
    memberId,
    policyNumber,
    status,
    effectiveDate,
    expirationDate,
    insurer,
    benefitTemplateName,
    benefitTemplateAnnualLimitAmount,
    benefitTemplateGeographicCoverage,
    frontlineUser,
  } = policyItem;
  const statusVariant = getOrderStatusVariant(status);
  // const hotline = getInsurerHotlines(insurer, insuranceType);
  const claimProcedureLink =
    insuranceType === InsuranceTypesEnum.HLTH ? 'https://mgacover.com/en/articles/health/29' : '#';

  return (
    <Card border={statusVariant}>
      <Card.Header className="d-flex flex-row justify-content-between align-items-center bg-white">
        <h5 className="mb-0">{tInsuranceTypeEnum(insuranceType)}</h5>
        <InsurerLogo insurer={insurer} fluid />
      </Card.Header>
      <Card.Body>
        <RowItem label={t('common:memberName')} value={name} />
        <RowItem label={t('common:memberId')} value={memberId} />
        <RowItem label={t('common:policyNumber')} value={policyNumber} />
        <RowItem label={t('common:plan')} value={benefitTemplateName} />
        <RowItem
          label={t('common:periodOfInsurance')}
          value={`${formatDate(effectiveDate)} - ${formatDate(expirationDate)}`}
        />
        <RowItem label={t('digitalCard.annualLimit')} value={formatCurrency(benefitTemplateAnnualLimitAmount)} />
        <RowItem label={t('digitalCard.geographicCoverage')} value={benefitTemplateGeographicCoverage} />

        {/* <div className="mb-2">
          <small>
            <Trans i18nKey="common:emergencyCall" values={{ hotline }} />
          </small>
        </div> */}

        <Stack gap={2}>
          <div>
            <small style={{ fontWeight: 'bold' }}>{t('digitalCard.forReimbursement')}: </small>
            <small>
              {frontlineUser?.name}, {t('common:telephone')}: {frontlineUser?.mobileNumber}
            </small>
          </div>
          <div>
            <small style={{ fontWeight: 'bold' }}>{t('digitalCard.forDirectBilling')}: </small>
            <small>
              {t('common:telephone')}: {insurer?.telNumber || '-'}
            </small>
          </div>
          <div>
            <a href={claimProcedureLink} target="_blank" rel="noreferrer">
              <small style={{ fontWeight: 'bold' }}>{t('common:insuranceClaimProcedure')}</small>
            </a>
          </div>
        </Stack>

        <Stack direction="horizontal" gap={2} className="mt-3">
          <Button
            as={Link}
            to={routeUserDashboardInsurancePolicyUserDetailsPage({ insurancePolicyUserId: id })}
            variant="outline-primary"
          >
            {startCase(t('common:viewDetails'))}
          </Button>

          <Button
            as={Link}
            to={routeUserDashboardInsurancePolicyClaim({ insurancePolicyId, insurancePolicyUserId: id })}
            variant="outline-primary"
          >
            {t('common:claim')}
          </Button>
        </Stack>
      </Card.Body>
    </Card>
  );
};

export { PolicyItemItem };
